import { ToggleButtonGroup } from "@mui/material";
import { useNavigate } from "@tanstack/react-router";
import { useTranslations } from "use-intl";

import {
  IconFlightService,
  IconHotelService,
  IconTripService,
} from "@/components/Icons";
import {
  FlightSearchTrackingEvents,
  HotelSearchTrackingEvents,
  rudderAnalytics,
  TripTrackingEvents,
} from "@/lib/rudderstack";
import type { SearchScope } from "@/types/types";
import { Feature, isFeatureDisabled } from "@/utils/featureFlags";

import { ScopeSwitcherButton } from "./ScopeSwitcherButton";

interface ScopeSwitcherProps {
  current: SearchScope;
}

const eventMap = {
  flights: FlightSearchTrackingEvents.USER_SWITCHED_TO_FLIGHT_SEARCH,
  hotels: HotelSearchTrackingEvents.USER_SWITCHED_TO_HOTEL_SEARCH,
  trips: TripTrackingEvents.USER_SWITCHED_TO_TRIPS,
} as const;

export const ScopeSwitcher = ({ current }: ScopeSwitcherProps) => {
  const t = useTranslations();
  const navigate = useNavigate();
  const isHidden =
    isFeatureDisabled(Feature.HotelsFlow) &&
    isFeatureDisabled(Feature.TripsFlow);

  return (
    <ToggleButtonGroup
      value={current}
      exclusive
      onChange={(_, newValue: SearchScope | null) => {
        if (newValue && eventMap[newValue]) {
          rudderAnalytics.track(eventMap[newValue]);
        }
        navigate({
          to: "/",
          search: {
            category: newValue || current,
          },
          replace: true,
        });
      }}
      aria-label="Service"
      sx={{
        display: "flex",
        gap: "calc(var(--spacing)*6)",
        visibility: isHidden ? "hidden" : "visible",
      }}
    >
      <ScopeSwitcherButton value="flights">
        <IconFlightService aria-hidden className="size-9 shrink-0" />
        {t("flights.title")}
      </ScopeSwitcherButton>

      {isFeatureDisabled(Feature.HotelsFlow) ? null : (
        <ScopeSwitcherButton value="hotels">
          <IconHotelService aria-hidden className="size-9 shrink-0" />
          {t("hotels.title")}
        </ScopeSwitcherButton>
      )}

      {isFeatureDisabled(Feature.TripsFlow) ? null : (
        <ScopeSwitcherButton value="trips" badgeContent={t("trips.newBadge")}>
          <IconTripService aria-hidden className="size-9 shrink-0" />
          {t("trips.title")}
        </ScopeSwitcherButton>
      )}
    </ToggleButtonGroup>
  );
};
